import React, { useEffect, useState } from 'react';
import { FaTimes } from 'react-icons/fa';
import { db } from '../firebase';
import { collection, addDoc, updateDoc, doc, getDocs, query, where } from 'firebase/firestore';
import { ZONE_CALCULATION_METHODS, SPORT_TYPES, getSportTypeLabel } from '../utils/trainingTypes';
import {
  calculateHRZonesFromMax,
  calculateHRZonesFromLTHR,
  calculatePowerZonesFromFTP,
  calculatePaceZonesFromThreshold,
  estimateMaxHRFromAge,
  estimateFTPFromMaxPower,
  paceToSeconds,
  secondsToPace
} from '../utils/zoneCalculator';

const overlayBaseStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0,0,0,0.6)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 20000,
};

const modalStyle = {
  backgroundColor: '#0f0f0f',
  color: '#e0e0e0',
  padding: '24px',
  borderRadius: '12px',
  border: '1px solid #2a2a2a',
  width: '92vw',
  maxWidth: '700px',
  maxHeight: '90vh',
  overflowY: 'auto',
  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6)',
};

const btnStyle = {
  background: 'transparent',
  border: '1px solid #2a2a2a',
  color: '#e0e0e0',
  borderRadius: '8px',
  padding: '10px 16px',
  cursor: 'pointer',
  fontWeight: 500,
};

const primaryBtnStyle = {
  ...btnStyle,
  background: 'var(--brand-accent-gradient)',
  border: 'none',
  color: '#fff',
};

const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  backgroundColor: '#1a1a1a',
  border: '1px solid #2a2a2a',
  borderRadius: '8px',
  color: '#e0e0e0',
  fontSize: '14px',
};

export default function ZoneConfigModal({ isOpen, onClose, userId }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sport, setSport] = useState('cycling');
  const [method, setMethod] = useState('max');
  const [existingZones, setExistingZones] = useState(null);

  // HR inputs
  const [maxHR, setMaxHR] = useState('');
  const [lthr, setLthr] = useState('');
  const [restingHR, setRestingHR] = useState('');
  const [age, setAge] = useState('');

  // Power inputs
  const [ftp, setFtp] = useState('');
  const [maxPower, setMaxPower] = useState('');

  // Pace inputs
  const [thresholdPace, setThresholdPace] = useState(''); // MM:SS format

  // Calculated zones
  const [calculatedZones, setCalculatedZones] = useState(null);

  useEffect(() => {
    if (!isOpen || !userId) return;
    loadExistingZones();
  }, [isOpen, userId, sport]);

  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen]);

  useEffect(() => {
    calculateZones();
  }, [method, maxHR, lthr, ftp, maxPower, thresholdPace, age, sport]);

  async function loadExistingZones() {
    if (!userId) return;
    try {
      setLoading(true);
      const zonesQuery = query(
        collection(db, 'trainingZones'),
        where('userId', '==', userId),
        where('sport', '==', sport)
      );
      const snapshot = await getDocs(zonesQuery);
      if (!snapshot.empty) {
        const zoneData = snapshot.docs[0].data();
        setExistingZones({ id: snapshot.docs[0].id, ...zoneData });
        
        // Populate form with existing data
        if (zoneData.hrZones) {
          if (zoneData.hrZones.maxHR) setMaxHR(String(zoneData.hrZones.maxHR));
          if (zoneData.hrZones.lactateThresholdHR) setLthr(String(zoneData.hrZones.lactateThresholdHR));
          if (zoneData.hrZones.restingHR) setRestingHR(String(zoneData.hrZones.restingHR));
        }
        if (zoneData.powerZones) {
          if (zoneData.powerZones.ftp) setFtp(String(zoneData.powerZones.ftp));
          if (zoneData.powerZones.maxPower) setMaxPower(String(zoneData.powerZones.maxPower));
        }
        if (zoneData.paceZones) {
          if (zoneData.paceZones.thresholdPace) {
            setThresholdPace(secondsToPace(zoneData.paceZones.thresholdPace));
          }
        }
        if (zoneData.method) setMethod(zoneData.method);
      } else {
        setExistingZones(null);
      }
    } catch (error) {
      console.error('Error loading zones:', error);
      alert('Failed to load existing zones');
    } finally {
      setLoading(false);
    }
  }

  function calculateZones() {
    let zones = null;

    if (sport === 'cycling' || sport === 'mtb' || sport === 'moto') {
      if (method === 'max' && maxHR) {
        zones = calculateHRZonesFromMax(Number(maxHR));
      } else if (method === 'lthr' && lthr) {
        zones = calculateHRZonesFromLTHR(Number(lthr));
      } else if (method === 'ftp' && ftp) {
        zones = { powerZones: calculatePowerZonesFromFTP(Number(ftp)) };
      }
    } else if (sport === 'running') {
      if (method === 'max' && maxHR) {
        zones = calculateHRZonesFromMax(Number(maxHR));
      } else if (method === 'lthr' && lthr) {
        zones = calculateHRZonesFromLTHR(Number(lthr));
      } else if (method === 'lactate' && thresholdPace) {
        const paceSeconds = paceToSeconds(thresholdPace);
        if (paceSeconds) {
          zones = { paceZones: calculatePaceZonesFromThreshold(paceSeconds) };
        }
      }
    }

    setCalculatedZones(zones);
  }

  function handleEstimateMaxHR() {
    if (age) {
      const estimated = estimateMaxHRFromAge(Number(age));
      if (estimated) {
        setMaxHR(String(estimated));
        setMethod('max');
      }
    }
  }

  function handleEstimateFTP() {
    if (maxPower) {
      const estimated = estimateFTPFromMaxPower(Number(maxPower));
      if (estimated) {
        setFtp(String(estimated));
        setMethod('ftp');
      }
    }
  }

  async function handleSave() {
    if (!userId) return;

    if (!calculatedZones) {
      alert('Please enter values to calculate zones');
      return;
    }

    try {
      setSaving(true);
      const zoneData = {
        userId,
        sport,
        method,
        createdAt: new Date(),
        updatedAt: new Date(),
        ...calculatedZones
      };

      // Add input values for reference
      if (calculatedZones.hrZones || (calculatedZones.zone1 && !calculatedZones.powerZones)) {
        zoneData.hrZones = {
          ...(calculatedZones.hrZones || calculatedZones),
          maxHR: maxHR ? Number(maxHR) : undefined,
          lactateThresholdHR: lthr ? Number(lthr) : undefined,
          restingHR: restingHR ? Number(restingHR) : undefined,
        };
      }

      if (calculatedZones.powerZones) {
        zoneData.powerZones = {
          ...calculatedZones.powerZones,
          ftp: ftp ? Number(ftp) : undefined,
          maxPower: maxPower ? Number(maxPower) : undefined,
        };
      }

      if (calculatedZones.paceZones) {
        const paceSeconds = paceToSeconds(thresholdPace);
        zoneData.paceZones = {
          ...calculatedZones.paceZones,
          thresholdPace: paceSeconds || undefined,
        };
      }

      if (existingZones) {
        await updateDoc(doc(db, 'trainingZones', existingZones.id), zoneData);
      } else {
        await addDoc(collection(db, 'trainingZones'), zoneData);
      }

      alert('Zones saved successfully!');
      onClose();
    } catch (error) {
      console.error('Error saving zones:', error);
      alert('Failed to save zones');
    } finally {
      setSaving(false);
    }
  }

  function resetForm() {
    setMaxHR('');
    setLthr('');
    setRestingHR('');
    setAge('');
    setFtp('');
    setMaxPower('');
    setThresholdPace('');
    setCalculatedZones(null);
    setExistingZones(null);
    setMethod('max');
  }

  if (!isOpen) return null;

  const hrZones = calculatedZones?.hrZones || calculatedZones;
  const powerZones = calculatedZones?.powerZones;
  const paceZones = calculatedZones?.paceZones;

  return (
    <div style={overlayBaseStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0, color: '#e0e0e0' }}>Configure Training Zones</h2>
          <button
            onClick={onClose}
            style={{
              ...btnStyle,
              padding: '8px 10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <FaTimes />
          </button>
        </div>

        {loading && <div style={{ marginBottom: '20px', color: '#9aa0a6' }}>Loading...</div>}

        {/* Sport Selection */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', color: '#e0e0e0' }}>Sport</label>
          <select
            value={sport}
            onChange={(e) => setSport(e.target.value)}
            style={inputStyle}
          >
            {SPORT_TYPES.map((s) => (
              <option key={s} value={s}>{getSportTypeLabel(s)}</option>
            ))}
          </select>
        </div>

        {/* Method Selection */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', color: '#e0e0e0' }}>Calculation Method</label>
          <select
            value={method}
            onChange={(e) => setMethod(e.target.value)}
            style={inputStyle}
          >
            {ZONE_CALCULATION_METHODS.map((m) => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
        </div>

        {/* Heart Rate Inputs */}
        {(sport === 'cycling' || sport === 'running' || sport === 'mtb' || sport === 'moto') && (
          <div style={{ marginBottom: '20px' }}>
            <h3 style={{ marginBottom: '12px', color: '#e0e0e0', fontSize: '16px' }}>Heart Rate Zones</h3>
            
            {method === 'max' && (
              <div style={{ marginBottom: '12px' }}>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#9aa0a6' }}>
                      Maximum HR (bpm)
                    </label>
                    <input
                      type="number"
                      value={maxHR}
                      onChange={(e) => setMaxHR(e.target.value)}
                      placeholder="e.g. 190"
                      style={inputStyle}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#9aa0a6' }}>
                      Age (for estimate)
                    </label>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <input
                        type="number"
                        value={age}
                        onChange={(e) => setAge(e.target.value)}
                        placeholder="e.g. 30"
                        style={{ ...inputStyle, flex: 1 }}
                      />
                      <button onClick={handleEstimateMaxHR} style={btnStyle} type="button">
                        Estimate
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {method === 'lthr' && (
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#9aa0a6' }}>
                  Lactate Threshold HR (bpm)
                </label>
                <input
                  type="number"
                  value={lthr}
                  onChange={(e) => setLthr(e.target.value)}
                  placeholder="e.g. 165"
                  style={inputStyle}
                />
              </div>
            )}

            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#9aa0a6' }}>
                Resting HR (bpm) - Optional
              </label>
              <input
                type="number"
                value={restingHR}
                onChange={(e) => setRestingHR(e.target.value)}
                placeholder="e.g. 50"
                style={inputStyle}
              />
            </div>

            {/* Display calculated HR zones */}
            {hrZones && (
              <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#1a1a1a', borderRadius: '8px' }}>
                <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#e0e0e0' }}>Calculated HR Zones:</h4>
                {[1, 2, 3, 4, 5].map((zone) => {
                  const zoneData = hrZones[`zone${zone}`];
                  if (!zoneData) return null;
                  return (
                    <div key={zone} style={{ marginBottom: '4px', fontSize: '12px', color: '#9aa0a6' }}>
                      Zone {zone}: {zoneData.min} - {zoneData.max} bpm
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Power Zones (Cycling) */}
        {sport === 'cycling' && method === 'ftp' && (
          <div style={{ marginBottom: '20px' }}>
            <h3 style={{ marginBottom: '12px', color: '#e0e0e0', fontSize: '16px' }}>Power Zones</h3>
            <div style={{ marginBottom: '12px' }}>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#9aa0a6' }}>
                    FTP (watts)
                  </label>
                  <input
                    type="number"
                    value={ftp}
                    onChange={(e) => setFtp(e.target.value)}
                    placeholder="e.g. 250"
                    style={inputStyle}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#9aa0a6' }}>
                    Max Power (watts) - for estimate
                  </label>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <input
                      type="number"
                      value={maxPower}
                      onChange={(e) => setMaxPower(e.target.value)}
                      placeholder="e.g. 800"
                      style={{ ...inputStyle, flex: 1 }}
                    />
                    <button onClick={handleEstimateFTP} style={btnStyle} type="button">
                      Estimate
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Display calculated power zones */}
            {powerZones && (
              <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#1a1a1a', borderRadius: '8px' }}>
                <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#e0e0e0' }}>Calculated Power Zones:</h4>
                {[1, 2, 3, 4, 5].map((zone) => {
                  const zoneData = powerZones[`zone${zone}`];
                  if (!zoneData) return null;
                  return (
                    <div key={zone} style={{ marginBottom: '4px', fontSize: '12px', color: '#9aa0a6' }}>
                      Zone {zone}: {zoneData.min} - {zoneData.max} watts
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Pace Zones (Running) */}
        {sport === 'running' && method === 'lactate' && (
          <div style={{ marginBottom: '20px' }}>
            <h3 style={{ marginBottom: '12px', color: '#e0e0e0', fontSize: '16px' }}>Pace Zones</h3>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#9aa0a6' }}>
                Threshold Pace (MM:SS per km)
              </label>
              <input
                type="text"
                value={thresholdPace}
                onChange={(e) => setThresholdPace(e.target.value)}
                placeholder="e.g. 04:30"
                style={inputStyle}
              />
            </div>

            {/* Display calculated pace zones */}
            {paceZones && (
              <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#1a1a1a', borderRadius: '8px' }}>
                <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#e0e0e0' }}>Calculated Pace Zones:</h4>
                {[1, 2, 3, 4, 5].map((zone) => {
                  const zoneData = paceZones[`zone${zone}`];
                  if (!zoneData) return null;
                  return (
                    <div key={zone} style={{ marginBottom: '4px', fontSize: '12px', color: '#9aa0a6' }}>
                      Zone {zone}: {secondsToPace(zoneData.max)} - {secondsToPace(zoneData.min)} per km
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
          <button onClick={onClose} style={btnStyle} disabled={saving}>
            Cancel
          </button>
          <button onClick={handleSave} style={primaryBtnStyle} disabled={saving || !calculatedZones}>
            {saving ? 'Saving...' : existingZones ? 'Update Zones' : 'Save Zones'}
          </button>
        </div>
      </div>
    </div>
  );
}
